/**
 * Ombraz Product Filter System
 * This script handles the filtering functionality for Ombraz products.
 * It allows users to filter products based on frame styles, gear, frame sizes, and lens options.
 */

let isMobile = window.matchMedia("(max-width: 1199px)").matches;

// If need to debug, set to true
window.OMBRAZ_DEBUG = false;

// Main initialization on DOMContentLoaded
document.addEventListener("DOMContentLoaded", function () {
  // Set isMobile based on current viewport
  isMobile = window.matchMedia("(max-width: 1199px)").matches;

  initializeAccordion();
  initializeFilters();

  // Hide on-sale products by default on non-sale collection pages
  const currentUrl = window.location.pathname;
  const onSaleSection = document.querySelector("[data-onsale-products]");
  const isSalePage =
    currentUrl.includes("/collections/onsale") ||
    currentUrl.includes("/collections/on-sale");

  if (onSaleSection && !isSalePage) {
    onSaleSection.classList.add("hide-this", "visually-hidden");
    // Uncheck the sale checkbox if it exists
    const saleCheckbox = document.querySelector(
      'input[data-collection="onsale"]'
    );
    if (saleCheckbox) {
      saleCheckbox.checked = false;
    }
  }

  // First try to handle direct size URL parameter
  const sizeParameterHandled = handleSizeURLParameter();

  // If size parameter wasn't handled, check other URL parameters for filter state
  if (!sizeParameterHandled) {
    const filtersRestored = restoreFiltersFromURL();

    // If no filters were restored, check for collection-specific filtering
    if (!filtersRestored) {
      handleURLBasedFiltering();
    }
  }

  // Use a short delay to make sure DOM is updated
  setTimeout(() => {
    // Check if we have any active filters
    const hasActiveFilters =
      document.querySelectorAll('input[type="checkbox"]:checked').length > 0;

    if (!hasActiveFilters) {
      // If no filters are active, scroll to a specific position that shows products
      const targetScrollPosition = 653.5;

      window.scrollTo({
        top: targetScrollPosition,
        behavior: "smooth",
      });

      debugLog(`Scrolling to fixed position: ${targetScrollPosition}px`);
    }
    // If filters are active, scrolling is handled in the filter application
  }, 500);
});

// Update isMobile when window resizes
window.addEventListener("resize", function () {
  isMobile = window.matchMedia("(max-width: 1199px)").matches;
});

function saveFiltersToURL() {
  // Get active filters
  const frameStyles = getActiveFilters("frame-style");
  const frameSizes = getActiveFilters("frame-size");
  const lensOptions = getActiveFilters("lens-option");
  const frameColors = getActiveFilters("frame-color");
  const gearItems = getActiveFilters("gear");
  const saleChecked =
    document.querySelector('.ombraz-sale [data-collection="onsale"]')
      ?.checked || false;
  const bestSellerChecked =
    document.querySelector('.ombraz-best-seller input[type="checkbox"]')
      ?.checked || false;

  // Create URL parameters
  const params = new URLSearchParams();

  // Only add parameters that have values
  if (frameStyles.length > 0) params.set("styles", frameStyles.join(","));
  if (frameSizes.length > 0) params.set("sizes", frameSizes.join(","));
  if (lensOptions.length > 0) params.set("lenses", lensOptions.join(","));
  if (frameColors.length > 0) params.set("colors", frameColors.join(","));
  if (gearItems.length > 0) params.set("gear", gearItems.join(","));
  if (saleChecked) params.set("sale", "true");
  if (bestSellerChecked) params.set("bestseller", "true");

  // Update the URL without refreshing
  if (params.toString()) {
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
    debugLog("Filter state saved to URL", params.toString());
  } else {
    // If no filters, remove any query parameters
    window.history.replaceState({}, "", window.location.pathname);
  }
}

// Improved restoreFiltersFromURL function
function restoreFiltersFromURL() {
  debugLog("Starting restoreFiltersFromURL");

  // Get parameters from URL
  const params = new URLSearchParams(window.location.search);
  let hasRestoredFilters = false;

  // Create an object to track which filters we've restored
  const restoredFilters = {
    "frame-style": [],
    "frame-size": [],
    "lens-option": [],
    "frame-color": [],
    gear: [],
  };

  // Helper function to check filter options
  function checkFilter(type, paramName) {
    if (params.has(paramName)) {
      const values = params.get(paramName).split(",");
      values.forEach((value) => {
        const normalizedValue = value.toLowerCase().trim();
        debugLog(`Checking filter ${type} with value ${normalizedValue}`);

        // Add to our tracking object
        restoredFilters[type].push(normalizedValue);

        const checkbox = document.querySelector(
          `input[data-filter-type="${type}"][data-${type}="${normalizedValue}"]`
        );

        if (checkbox) {
          checkbox.checked = true;
          // Open accordion containing this checkbox
          const accordion = checkbox.closest(".ombraz-accordion-item");
          if (accordion) {
            accordion.setAttribute("open", "");
            updateAccordionIcon(accordion, true);
          }
          hasRestoredFilters = true;
          debugLog(`Restored filter ${type}=${normalizedValue}`);
        } else {
          debugLog(`Could not find checkbox for ${type}=${normalizedValue}`);
        }
      });
      return true; // Indicate that we found and processed this parameter
    }
    return false; // Parameter not found
  }

  // Check all filter types
  checkFilter("frame-style", "styles");
  checkFilter("frame-size", "sizes");
  checkFilter("lens-option", "lenses");
  checkFilter("frame-color", "colors");
  checkFilter("gear", "gear");

  // Restore sale checkbox
  if (params.has("sale") && params.get("sale") === "true") {
    const saleCheckbox = document.querySelector(
      '.ombraz-sale [data-collection="onsale"]'
    );
    if (saleCheckbox) {
      saleCheckbox.checked = true;
      hasRestoredFilters = true;
      debugLog("Restored sale filter");
    } else {
      debugLog("Could not find sale checkbox");
    }
  }

  // Restore best seller checkbox
  if (params.has("bestseller") && params.get("bestseller") === "true") {
    const bestSellerCheckbox = document.querySelector(
      '.ombraz-best-seller input[type="checkbox"]'
    );
    if (bestSellerCheckbox) {
      bestSellerCheckbox.checked = true;
      hasRestoredFilters = true;
      debugLog("Restored bestseller filter");
    } else {
      debugLog("Could not find bestseller checkbox");
    }
  }

  // If we have restored any filters, apply them with a delay to ensure DOM is ready
  if (hasRestoredFilters) {
    debugLog(
      "Restored filters from URL parameters, applying filters with delay",
      restoredFilters
    );

    // Toggle UI buttons
    toggleShowResultsButton(true);
    toggleClearButton(true);

    // Use a longer delay to ensure DOM is fully loaded
    setTimeout(() => {
      // Apply the filters
      const bestSellerChecked =
        document.querySelector('.ombraz-best-seller input[type="checkbox"]')
          ?.checked || false;
      const saleChecked =
        document.querySelector('.ombraz-sale [data-collection="onsale"]')
          ?.checked || false;

      // Force filter application regardless of mobile/desktop state
      if (bestSellerChecked) {
        handleBestSellerFilter(true); // Pass true to force application
      } else if (saleChecked) {
        handleSaleFilter(true); // Pass true to force application
      } else {
        // Apply filters with special handling for multiple filter types
        applyFiltersFromURL(restoredFilters);
      }

      // For mobile, we need to close the filter header if it's open
      if (isMobile) {
        const sidebar = document.getElementById("ombrazSidebar");
        if (sidebar && sidebar.classList.contains("active")) {
          closeTheFilterHederMobile();
        }

        // Scroll to first visible item
        setTimeout(() => {
          const visibleItem =
            document.querySelector(
              ".ombraz-product-v2__grid-item:not(.hide-this)"
            ) ||
            document.querySelector(".is-onsale-product-card:not(.hide-this)") ||
            document.querySelector(
              ".bs-product-card-v2__list-item:not(.hide-this)"
            );

          if (visibleItem) {
            scrollToElement(visibleItem);
          }
        }, 300);
      }
    }, 500); // Increased delay to ensure DOM is ready

    return true;
  }

  return false;
}

// New function to apply filters from URL with special handling for multiple filter types
function applyFiltersFromURL(restoredFilters) {
  debugLog(
    "applyFiltersFromURL: Starting filter application with",
    restoredFilters
  );

  // Get product grid and all items
  const productGrid = document.querySelector(".ombraz-product-v2__grid");
  const allItems = productGrid
    ? productGrid.querySelectorAll(".ombraz-product-v2__grid-item")
    : [];

  // Hide or show the product items based on filters
  let visibleCount = 0;

  // Create a set to track unique product keys to avoid duplicates in results
  const seenProductKeys = new Set();

  // Check if any filter type is active
  const hasFrameStyleFilters = restoredFilters["frame-style"].length > 0;
  const hasFrameSizeFilters = restoredFilters["frame-size"].length > 0;
  const hasLensOptionFilters = restoredFilters["lens-option"].length > 0;
  const hasFrameColorFilters = restoredFilters["frame-color"].length > 0;
  const hasGearFilters = restoredFilters["gear"].length > 0;

  const anyFilterActive =
    hasFrameStyleFilters ||
    hasFrameSizeFilters ||
    hasLensOptionFilters ||
    hasFrameColorFilters ||
    hasGearFilters;

  debugLog("applyFiltersFromURL: Filter status", {
    hasFrameStyleFilters,
    hasFrameSizeFilters,
    hasLensOptionFilters,
    hasFrameColorFilters,
    hasGearFilters,
    anyFilterActive,
  });

  // Apply filters to each product item
  allItems.forEach((item) => {
    // For debugging purpose
    const itemTitle = (item.getAttribute("data-title") || "").toLowerCase();
    const itemGear = (item.getAttribute("data-gear") || "").toLowerCase();
    const itemStyle = (item.getAttribute("data-style") || "").toLowerCase();
    const itemColor = (item.getAttribute("data-color") || "").toLowerCase();
    const itemLensValue = (
      item.getAttribute("data-lens-value") || ""
    ).toLowerCase();

    // Get available sizes using our helper function
    const itemSizes = getAvailableSizes(item);

    debugLog(`Checking product: ${itemTitle}`, {
      itemStyle,
      itemSizes,
      itemLensValue,
      itemColor,
      itemGear,
    });

    // Check if this is a gear/accessory item
    const isGearOrAccessory =
      itemGear !== "" ||
      itemStyle.includes("shields") ||
      itemStyle.includes("riser") ||
      itemTitle.includes("side shields") ||
      itemTitle.includes("nose pad") ||
      itemTitle.includes("risers") ||
      itemTitle.includes("floatie") ||
      itemTitle.includes("case") ||
      itemTitle.includes("cord") ||
      itemTitle.includes("sticker") ||
      itemTitle.includes("gift card");

    // Special handling for gear items when gear filters are active
    if (hasGearFilters) {
      // If this is a gear item, check if it matches the selected gear filters
      if (isGearOrAccessory) {
        const matchesGear = restoredFilters["gear"].some(
          (gearType) =>
            itemGear.includes(gearType) ||
            (gearType === "shields" &&
              (itemTitle.includes("side shields") ||
                itemStyle.includes("shields"))) ||
            (gearType === "risers" &&
              (itemTitle.includes("nose pad") ||
                itemTitle.includes("risers") ||
                itemStyle.includes("riser"))) ||
            (gearType === "floatie" && itemTitle.includes("floatie")) ||
            (gearType === "case" && itemTitle.includes("case")) ||
            (gearType === "cordreplacement" && itemTitle.includes("cord")) ||
            (gearType === "sticker" && itemTitle.includes("sticker")) ||
            (gearType === "giftcard" && itemTitle.includes("gift card"))
        );

        if (matchesGear) {
          item.classList.remove("hide-this");
          visibleCount++;
          debugLog(`Showing gear item: ${itemTitle}`);
          return; // Skip other checks, this is a matched gear item
        } else {
          item.classList.add("hide-this");
          debugLog(`Hiding gear item (no match): ${itemTitle}`);
          return; // Skip other checks, this is an unmatched gear item
        }
      } else {
        // This is not a gear item but gear filter is active, hide it
        item.classList.add("hide-this");
        debugLog(`Hiding non-gear item: ${itemTitle}`);
        return;
      }
    }

    // If gear filter is not active but this is a gear item and other filters are active,
    // we'll skip regular product checks as they don't apply to gear
    if (
      isGearOrAccessory &&
      (hasFrameSizeFilters ||
        hasFrameStyleFilters ||
        hasLensOptionFilters ||
        hasFrameColorFilters)
    ) {
      item.classList.add("hide-this");
      debugLog(`Hiding gear item (other filters active): ${itemTitle}`);
      return;
    }

    // For regular products (non-gear items)
    let matchesAllFilters = true;

    // Check frame style filters
    if (hasFrameStyleFilters) {
      if (!restoredFilters["frame-style"].includes(itemStyle)) {
        matchesAllFilters = false;
        debugLog(
          `Style mismatch for ${itemTitle}: ${itemStyle} not in ${restoredFilters["frame-style"]}`
        );
      }
    }

    // Check frame size filters
    if (matchesAllFilters && hasFrameSizeFilters) {
      const matchesSize = restoredFilters["frame-size"].some((size) => {
        const lowerSize = size.toLowerCase();

        // Enhanced size matching with more flexible comparison
        if (lowerSize === "narrow" || lowerSize === "narrow/regular") {
          return itemSizes.some(
            (s) =>
              s.includes("narrow") || s.includes("n/r") || s.includes("n-r")
          );
        } else if (lowerSize === "regular") {
          return itemSizes.some(
            (s) => s.includes("regular") || s.includes("reg")
          );
        } else if (lowerSize === "xl") {
          return itemSizes.some(
            (s) => s.includes("xl") || s.includes("extra large")
          );
        }

        return itemSizes.includes(lowerSize);
      });

      if (!matchesSize) {
        matchesAllFilters = false;
        debugLog(
          `Size mismatch for ${itemTitle}: ${itemSizes} does not match any of ${restoredFilters["frame-size"]}`
        );
      }
    }

    // Check lens option filters
    if (matchesAllFilters && hasLensOptionFilters) {
      const matchesLensOption = restoredFilters["lens-option"].some(
        (option) => {
          if (option === "polarized") {
            return (
              itemLensValue.includes("polarized") &&
              !itemLensValue.includes("non-polarized")
            );
          } else if (option === "non-polarized") {
            return itemLensValue.includes("non-polarized");
          } else if (option === "blue-blocking") {
            return itemLensValue.includes("blue-blocking");
          }

          return itemLensValue.includes(option);
        }
      );

      if (!matchesLensOption) {
        matchesAllFilters = false;
        debugLog(
          `Lens mismatch for ${itemTitle}: ${itemLensValue} does not match any of ${restoredFilters["lens-option"]}`
        );
      }
    }

    // Check frame color filters
    if (matchesAllFilters && hasFrameColorFilters) {
      if (!restoredFilters["frame-color"].includes(itemColor)) {
        matchesAllFilters = false;
        debugLog(
          `Color mismatch for ${itemTitle}: ${itemColor} not in ${restoredFilters["frame-color"]}`
        );
      }
    }

    // If this item matches all filters, check for duplicates
    if (matchesAllFilters) {
      // Create a unique product key for deduplication
      const productKey = `${itemStyle}-${itemSizes.join(
        ","
      )}-${itemLensValue}-${itemColor}`;

      if (seenProductKeys.has(productKey)) {
        // This is a duplicate product, hide it
        debugLog(
          `Hiding duplicate product: ${itemTitle} with key ${productKey}`
        );
        item.classList.add("hide-this");
      } else {
        // First time seeing this product, add to tracking set
        seenProductKeys.add(productKey);
        item.classList.remove("hide-this");
        visibleCount++;
        debugLog(`Showing product: ${itemTitle}`);
      }
    } else {
      // Not matching the filters, hide it
      item.classList.add("hide-this");
      debugLog(`Hiding product (filter mismatch): ${itemTitle}`);
    }
  });

  debugLog(
    `applyFiltersFromURL: Filter applied, ${visibleCount} items visible`
  );

  // Update UI states
  if (productGrid) {
    productGrid.classList.toggle("is-filtered-now", anyFilterActive);
  }

  // Show or hide "No results" message
  const showNoResults = visibleCount === 0 && anyFilterActive;
  displayNoResultsMessage(showNoResults);

  // If any filter is active, scroll to the ideal position on desktop
  if (anyFilterActive && !isMobile) {
    setTimeout(() => {
      window.scrollTo({
        top: 653.5,
        behavior: "smooth",
      });
    }, 200);
  }
}

function initializeFilters() {
  const filterToggle = document.getElementById("ombrazFilterHeader");
  if (filterToggle) {
    filterToggle.addEventListener("click", toggleFilter);
  }

  const filterCheckboxes = document.querySelectorAll(
    '.ombraz-accordion-content input[type="checkbox"]'
  );
  const bestSellerCheckbox = document.querySelector(
    '.ombraz-best-seller input[type="checkbox"]'
  );
  const productItems = document.querySelectorAll(
    ".ombraz-product-v2__grid-item"
  );
  const saleCheckbox = document.querySelector(
    '.ombraz-sale input[type="checkbox"]'
  );

  // Hide both buttons initially
  toggleClearButton(false);
  toggleShowResultsButton(false);

  // Add change event listener to Best Seller checkbox
  if (bestSellerCheckbox) {
    bestSellerCheckbox.addEventListener("change", () => {
      // Uncheck sale checkbox if it exists and is checked
      if (saleCheckbox && saleCheckbox.checked) {
        saleCheckbox.checked = false;

        // Also hide sale products
        const saleProducts = document.querySelector("[data-onsale-products]");
        if (saleProducts) {
          saleProducts.classList.add("hide-this", "visually-hidden");
        }
      }

      // Show Show Results button on mobile
      toggleShowResultsButton(bestSellerCheckbox.checked);

      // Handle filtering based on device
      if (!isMobile) {
        // On desktop, apply filter immediately
        handleBestSellerFilter();
      } else if (bestSellerCheckbox.checked) {
        // On mobile with checkbox checked, scroll to show results button
        scrollToShowResults();
      }

      // Save filter state to URL
      saveFiltersToURL();
    });
  }

  // Add change event listener to Sale checkbox
  if (saleCheckbox) {
    saleCheckbox.addEventListener("change", () => {
      // Uncheck best seller checkbox if it exists and is checked
      if (bestSellerCheckbox && bestSellerCheckbox.checked) {
        bestSellerCheckbox.checked = false;

        // Also hide best seller products
        const bestSellerProducts = document.querySelector(
          "[data-custom-products]"
        );
        if (bestSellerProducts) {
          bestSellerProducts.classList.add("hide-this", "visually-hidden");
        }
      }

      // Show Show Results button
      toggleShowResultsButton(saleCheckbox.checked);

      // Handle filtering based on device
      if (!isMobile) {
        // On desktop, apply filter immediately
        handleSaleFilter();
      } else if (saleCheckbox.checked) {
        // On mobile with checkbox checked, scroll to show results button
        scrollToShowResults();
      }

      // Save filter state to URL
      saveFiltersToURL();
    });
  }

  // Show all products by default
  if (productItems.length) {
    productItems.forEach((item) => item.classList.remove("hide-this"));
  }

  // Add checkbox change event listener
  if (filterCheckboxes.length) {
    filterCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", (event) => {
        // Get Sale checkbox
        const saleCheckbox = document.querySelector(
          '.ombraz-sale input[type="checkbox"]'
        );

        // If sale is checked when another filter is checked
        if (saleCheckbox && saleCheckbox.checked && checkbox.checked) {
          // Apply both sale and other filters together
          handleSaleFilter();
        }

        // If Best Seller is checked when another filter is checked
        if (
          bestSellerCheckbox &&
          bestSellerCheckbox.checked &&
          checkbox.checked
        ) {
          // Apply both Best Seller and other filters together
          handleBestSellerFilter();
        }

        // Check if any checkbox is checked
        const hasCheckedBoxes = Array.from(filterCheckboxes).some(
          (cb) => cb.checked
        );

        // Show/hide the buttons based on checked state
        toggleShowResultsButton(hasCheckedBoxes);
        toggleClearButton(hasCheckedBoxes);

        if (!isMobile) {
          applyFilters(event);
        } else if (hasCheckedBoxes) {
          // Scroll to show results button on mobile when checking a filter
          scrollToShowResults();
        }

        // Save filter state to URL
        saveFiltersToURL();
      });
    });
  }

  const showResultsBtn = document.getElementById("show-results");
  if (showResultsBtn) {
    showResultsBtn.addEventListener("click", () => {
      const bestSellerCheckbox = document.querySelector(
        '.ombraz-best-seller input[type="checkbox"]'
      );
      const saleCheckbox = document.querySelector(
        '.ombraz-sale input[type="checkbox"]'
      );

      // Check if both checkboxes are checked (this shouldn't happen, but let's handle it)
      if (bestSellerCheckbox?.checked && saleCheckbox?.checked) {
        // Prioritize Best Seller in this case
        saleCheckbox.checked = false;

        // Hide sale products
        const saleProducts = document.querySelector("[data-onsale-products]");
        if (saleProducts) {
          saleProducts.classList.add("hide-this", "visually-hidden");
        }
      }

      // Handle the appropriate filter based on which checkbox is checked
      if (bestSellerCheckbox && bestSellerCheckbox.checked) {
        handleBestSellerFilter(true); // Pass true to indicate from Show Results
      } else if (saleCheckbox && saleCheckbox.checked) {
        handleSaleFilter(true); // Pass true to indicate from Show Results
      } else {
        applyFilters();
      }

      // Close filter panel on mobile
      if (isMobile) {
        closeTheFilterHederMobile();
      }

      // Save filter state to URL
      saveFiltersToURL();
    });
  }

  // If no filters from URL parameters, check for collection-specific filtering
  if (!restoreFiltersFromURL()) {
    handleURLBasedFiltering();
  }

  // Initialize the accordion
  initializeAccordion();

  // Clear all filters
  clearAllFilters();
}

// Debug helper function to log filter issues to console
function debugLog(message, data = null) {
  if (window.OMBRAZ_DEBUG) {
    if (data) {
      console.log(`OMBRAZ DEBUG: ${message}`, data);
    } else {
      console.log(`OMBRAZ DEBUG: ${message}`);
    }
  }
}

function applyFilters(event) {
  debugLog("applyFilters: Starting filter application");

  // Get all filter inputs
  const filterInputs = document.querySelectorAll("input[data-filter-type]");

  // Check if any filter is checked
  const anyFilterActive = Array.from(filterInputs).some(
    (input) => input.checked
  );

  // Get all checked filter values
  const checkedFrameStyles = getActiveFilters("frame-style");
  const checkedFrameSizes = getActiveFilters("frame-size");
  const checkedLensOptions = getActiveFilters("lens-option");
  const checkedFrameColors = getActiveFilters("frame-color");
  const checkedGearItems = getActiveFilters("gear");

  // Check if any gear filter is active
  const isGearFilterActive = checkedGearItems.length > 0;

  // Check if the on-sale checkbox is checked
  const onSaleChecked =
    document.querySelector('.ombraz-sale [data-collection="onsale"]')
      ?.checked || false;

  // Create a set to track unique product keys to avoid duplicates in results
  const seenProductKeys = new Set();

  debugLog("applyFilters: Active filters", {
    anyFilterActive,
    checkedFrameStyles,
    checkedFrameSizes,
    checkedLensOptions,
    checkedFrameColors,
    checkedGearItems,
    onSaleChecked,
  });

  // Get product grid and all items
  const productGrid = document.querySelector(".ombraz-product-v2__grid");
  const allItems = productGrid
    ? productGrid.querySelectorAll(".ombraz-product-v2__grid-item")
    : [];

  // Hide or show the product items based on filters
  let visibleCount = 0;

  allItems.forEach((item) => {
    // For debugging purpose
    const itemTitle = (item.getAttribute("data-title") || "").toLowerCase();
    const itemGear = (item.getAttribute("data-gear") || "").toLowerCase();
    const itemStyle = (item.getAttribute("data-style") || "").toLowerCase();

    // Check if this is a gear/accessory item
    const isGearOrAccessory =
      itemGear !== "" ||
      itemStyle.includes("shields") ||
      itemStyle.includes("riser") ||
      itemTitle.includes("side shields") ||
      itemTitle.includes("nose pad") ||
      itemTitle.includes("risers") ||
      itemTitle.includes("floatie") ||
      itemTitle.includes("case") ||
      itemTitle.includes("cord") ||
      itemTitle.includes("sticker") ||
      itemTitle.includes("gift card");

    // Special handling for gear items when gear filters are active
    if (isGearFilterActive) {
      // If this is a gear item, check if it matches the selected gear filters
      if (isGearOrAccessory) {
        const matchesGear = checkedGearItems.some(
          (gearType) =>
            itemGear.includes(gearType) ||
            (gearType === "shields" &&
              (itemTitle.includes("side shields") ||
                itemStyle.includes("shields"))) ||
            (gearType === "risers" &&
              (itemTitle.includes("nose pad") ||
                itemTitle.includes("risers") ||
                itemStyle.includes("riser"))) ||
            (gearType === "floatie" && itemTitle.includes("floatie")) ||
            (gearType === "case" && itemTitle.includes("case")) ||
            (gearType === "cordreplacement" && itemTitle.includes("cord")) ||
            (gearType === "sticker" && itemTitle.includes("sticker")) ||
            (gearType === "giftcard" && itemTitle.includes("gift card"))
        );

        if (matchesGear) {
          item.classList.remove("hide-this");
          visibleCount++;
          return; // Skip other checks, this is a matched gear item
        } else {
          item.classList.add("hide-this");
          return; // Skip other checks, this is an unmatched gear item
        }
      } else {
        // This is not a gear item but gear filter is active, hide it
        item.classList.add("hide-this");
        return;
      }
    }

    // If gear filter is not active but this is a gear item and other filters are active,
    // we'll skip regular product checks as they don't apply to gear
    if (
      isGearOrAccessory &&
      (checkedFrameSizes.length > 0 ||
        checkedFrameStyles.length > 0 ||
        checkedLensOptions.length > 0 ||
        checkedFrameColors.length > 0)
    ) {
      item.classList.add("hide-this");
      return;
    }

    // For regular products (non-gear items)
    let matchesAllFilters = true;

    // Check frame size filters
    if (checkedFrameSizes.length > 0) {
      const itemSize = (item.getAttribute("data-size") || "").toLowerCase();
      const itemSizes = (item.getAttribute("data-sizes") || "")
        .toLowerCase()
        .split(",")
        .map((s) => s.trim());
      const itemTitle = (item.getAttribute("data-title") || "").toLowerCase();

      const matchesSize = checkedFrameSizes.some((size) => {
        const lowerSize = size.toLowerCase();

        // Enhanced size matching
        if (lowerSize === "narrow" || lowerSize === "narrow/regular") {
          return (
            itemSize.includes("narrow") ||
            itemSizes.some((s) => s.includes("narrow")) ||
            itemSize.includes("n/r") ||
            itemSize.includes("n-r") ||
            itemTitle.includes("narrow")
          );
        } else if (lowerSize === "regular") {
          return (
            itemSize.includes("regular") ||
            itemSizes.some((s) => s.includes("regular")) ||
            itemSize.includes("reg") ||
            itemTitle.includes("regular")
          );
        } else if (lowerSize === "xl") {
          return (
            itemSize.includes("xl") ||
            itemSizes.some((s) => s.includes("xl")) ||
            itemSize.includes("extra large") ||
            itemTitle.includes("xl")
          );
        }

        return itemSize === lowerSize || itemSizes.includes(lowerSize);
      });

      if (!matchesSize) {
        matchesAllFilters = false;
      }
    }

    // Check frame style filters
    if (matchesAllFilters && checkedFrameStyles.length > 0) {
      const itemStyle = (item.getAttribute("data-style") || "").toLowerCase();
      if (!checkedFrameStyles.includes(itemStyle)) {
        matchesAllFilters = false;
      }
    }

    // Check lens option filters
    if (matchesAllFilters && checkedLensOptions.length > 0) {
      const itemLensValue = (
        item.getAttribute("data-lens-value") || ""
      ).toLowerCase();
      const matchesLensOption = checkedLensOptions.some((option) => {
        if (option === "polarized") {
          return (
            itemLensValue.includes("polarized") &&
            !itemLensValue.includes("non-polarized")
          );
        } else if (option === "non-polarized") {
          return itemLensValue.includes("non-polarized");
        } else if (option === "blue-blocking") {
          return itemLensValue.includes("blue-blocking");
        }

        return itemLensValue.includes(option);
      });

      if (!matchesLensOption) {
        matchesAllFilters = false;
      }
    }

    // Check frame color filters
    if (matchesAllFilters && checkedFrameColors.length > 0) {
      const itemColor = (item.getAttribute("data-color") || "").toLowerCase();
      if (!checkedFrameColors.includes(itemColor)) {
        matchesAllFilters = false;
      }
    }

    // Check on-sale filter if active
    if (matchesAllFilters && onSaleChecked) {
      const compareAtPrice = item.querySelector(
        ".ombraz-product-v2__compare-at-price"
      );
      const itemCompareAtPrice = item.getAttribute("data-compare-at-price");

      // If on-sale is checked, only show items with a compare-at price
      if (!compareAtPrice || !itemCompareAtPrice || itemCompareAtPrice === "") {
        matchesAllFilters = false;
      }
    }

    // If this item matches all filters, check for duplicates
    if (matchesAllFilters) {
      const itemStyle = (item.getAttribute("data-style") || "").toLowerCase();
      const itemSize = (item.getAttribute("data-size") || "").toLowerCase();
      const itemLensValue = (
        item.getAttribute("data-lens-value") || ""
      ).toLowerCase();
      const itemColor = (item.getAttribute("data-color") || "").toLowerCase();

      // Create a unique product key for deduplication
      const productKey = `${itemStyle}-${itemSize}-${itemLensValue}-${itemColor}`;

      if (seenProductKeys.has(productKey)) {
        // This is a duplicate product, hide it
        debugLog(
          `Hiding duplicate product: ${item.getAttribute(
            "data-title"
          )} with key ${productKey}`
        );
        item.classList.add("hide-this");
      } else {
        // First time seeing this product, add to tracking set
        seenProductKeys.add(productKey);
        item.classList.remove("hide-this");
        visibleCount++;
      }
    } else {
      // Not matching the filters, hide it
      item.classList.add("hide-this");
    }
  });

  debugLog(`applyFilters: Filter applied, ${visibleCount} items visible`);

  // Update UI states
  if (productGrid) {
    productGrid.classList.toggle("is-filtered-now", anyFilterActive);
  }

  // Show or hide "No results" message
  const showNoResults = visibleCount === 0 && anyFilterActive;
  displayNoResultsMessage(showNoResults);

  // If any filter is active, scroll to the ideal position on desktop
  if (anyFilterActive && !isMobile) {
    setTimeout(() => {
      window.scrollTo({
        top: 653.5,
        behavior: "smooth",
      });
    }, 200);
  }

  // Update URL parameters to reflect current filter state
  saveFiltersToURL();
}

// Helper function to get active filters by type
function getActiveFilters(filterType) {
  const attributeName = `data-${filterType}`;

  // Get all checked checkboxes for this filter type
  const checkedCheckboxes = document.querySelectorAll(
    `input[data-filter-type="${filterType}"]:checked`
  );

  // Map to get the filter values
  return Array.from(checkedCheckboxes)
    .map((cb) => {
      // Get the value from the appropriate data attribute
      const value = cb.getAttribute(attributeName);
      return value ? value.toLowerCase() : "";
    })
    .filter((value) => value !== ""); // Remove any empty values
}

// Function to display "No Results" message
function displayNoResultsMessage(show) {
  debugLog(`displayNoResultsMessage called with show=${show}`);

  // Find the appropriate container to show the message in
  const container = findVisibleContainer();
  let messageElement = document.getElementById("noResultsMessage");

  if (show) {
    debugLog("Creating or showing No Results message");
    if (!messageElement) {
      // Create the message element if it doesn't exist
      messageElement = document.createElement("div");
      messageElement.id = "noResultsMessage";
      messageElement.className = "ombraz-no-results-message";

      messageElement.innerHTML = `
        <div class="ombraz-no-results-container">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M15 9L9 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 9L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <h3>No products match your selected filters</h3>
          <p>Try adjusting your filter criteria to see more products.</p>
          <button id="clearFiltersBtn" class="ombraz-clear-filters-btn">Clear All Filters</button>
        </div>
      `;

      // Append to the appropriate container
      if (container) {
        debugLog(
          `Appending No Results message to container: ${container.className}`
        );
        container.appendChild(messageElement);

        // Add event listener to the Clear Filters button
        const clearBtn = messageElement.querySelector("#clearFiltersBtn");
        if (clearBtn) {
          clearBtn.addEventListener("click", function () {
            // Simulate a click on the existing Clear All button
            const clearAllBtn =
              document.getElementById("clear-all") ||
              document.getElementById("clear-all-mobile");
            if (clearAllBtn) {
              clearAllBtn.click();
            }
          });
        }
      } else {
        debugLog("No container found to append No Results message");
      }
    } else {
      // If the message already exists, make sure it's in the correct container
      if (container && messageElement.parentElement !== container) {
        container.appendChild(messageElement);
      }
      messageElement.classList.remove("hide-this");
    }
  } else if (messageElement) {
    // Hide the message if it exists
    debugLog("Hiding No Results message");
    messageElement.classList.add("hide-this");
  }
}

// Helper function to find the appropriate container to show the "no results" message
function findVisibleContainer() {
  debugLog("Finding visible container for No Results message");

  const onSaleChecked =
    document.querySelector('.ombraz-sale [data-collection="onsale"]')
      ?.checked || false;
  const bestSellerChecked =
    document.querySelector('.ombraz-best-seller input[type="checkbox"]')
      ?.checked || false;

  const regularProductGrid = document.querySelector(".ombraz-product-v2__grid");
  const saleProductsContainer = document.querySelector(
    "[data-onsale-products]"
  );
  const bestSellerContainer = document.querySelector("[data-custom-products]");

  // Check which container is currently visible
  if (
    bestSellerChecked &&
    bestSellerContainer &&
    !bestSellerContainer.classList.contains("hide-this")
  ) {
    debugLog("Using best seller container for No Results message");
    return bestSellerContainer;
  } else if (
    onSaleChecked &&
    saleProductsContainer &&
    !saleProductsContainer.classList.contains("hide-this")
  ) {
    debugLog("Using sale products container for No Results message");
    return saleProductsContainer;
  } else if (
    regularProductGrid &&
    !regularProductGrid.classList.contains("hide-this")
  ) {
    debugLog("Using regular product grid for No Results message");
    return regularProductGrid;
  }

  // Fallback to the first available container in case none are visible
  return regularProductGrid || saleProductsContainer || bestSellerContainer;
}

// Helper function to check if an element is fully visible in viewport
function isElementInViewport(element) {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const windowHeight =
    window.innerHeight || document.documentElement.clientHeight;

  // Consider element visible if at least 200px of it is in the viewport
  // This provides a better UX by not scrolling when user can already see enough of the content
  const visibleHeight =
    Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
  return visibleHeight >= 200;
}

// Unified scrolling function to ensure consistent behavior
function scrollToElement(element) {
  if (!element) return;

  // Get the header for offset calculation
  const header = document.querySelector(".ombraz-filter-header");
  const headerHeight = header ? header.offsetHeight : 0;

  // Use very small offset to maximize product visibility
  const offset = headerHeight + 5;

  try {
    // Use modern scrollIntoView with options when available
    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });

    // Apply the offset with a small delay to ensure smooth animation
    setTimeout(() => {
      window.scrollBy({
        top: -offset,
        behavior: "smooth",
      });
    }, 50);
  } catch (error) {
    // Fallback for browsers that don't support scrollIntoView options
    const targetPosition =
      element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({
      top: targetPosition,
      behavior: "smooth",
    });
  }
}

// Function to scroll to the first visible product
function scrollToFirstVisibleProduct() {
  // Exit early if on mobile
  if (isMobile) return;

  const productGrid = document.querySelector(".ombraz-product-v2__grid");
  if (!productGrid) return;

  // If the grid itself is sufficiently visible, don't scroll
  if (isElementInViewport(productGrid)) {
    debugLog("Product grid is already visible, not scrolling");
    return;
  }

  // Check for visible products
  const visibleItem = productGrid.querySelector(
    ".ombraz-product-v2__grid-item:not(.hide-this)"
  );

  // If no visible products, check for No Results message
  if (!visibleItem) {
    const noResultsMessage = document.getElementById("noResultsMessage");
    if (noResultsMessage && !noResultsMessage.classList.contains("hide-this")) {
      if (!isElementInViewport(noResultsMessage)) {
        scrollToElement(noResultsMessage);
      }
    }
    return;
  }

  // Scroll to the first visible product
  scrollToElement(visibleItem);
}

// Simplified function to scroll to visible products based on filters
function scrollToVisibleProducts() {
  // Only scroll on desktop
  if (isMobile) return;

  // Choose the right container based on active filters
  const bestSellerChecked = document.querySelector(
    '.ombraz-best-seller input[type="checkbox"]'
  )?.checked;
  const saleChecked = document.querySelector(
    '.ombraz-sale input[type="checkbox"]'
  )?.checked;

  let container, targetItem;

  if (bestSellerChecked) {
    // Best Seller is active
    container = document.querySelector("[data-custom-products]");
    targetItem = container?.querySelector(
      ".bs-product-card-v2__list-item:not(.hide-this)"
    );
  } else if (saleChecked) {
    // Sale is active
    container = document.querySelector("[data-onsale-products]");
    targetItem = container?.querySelector(
      ".is-onsale-product-card:not(.hide-this)"
    );
  } else {
    // Regular products
    container = document.querySelector(".ombraz-product-v2__grid");
    targetItem = container?.querySelector(
      ".ombraz-product-v2__grid-item:not(.hide-this)"
    );
  }

  // If we have a container but it's already visible, don't scroll
  if (container && isElementInViewport(container)) {
    debugLog("Product container is already visible, not scrolling");
    return;
  }

  // Scroll to the target item or container
  if (targetItem) {
    scrollToElement(targetItem);
  } else if (container) {
    scrollToElement(container);
  }
}

// Replace scrollToBestSellerProducts with the unified scrollToElement
function scrollToBestSellerProducts(bestSellerProducts) {
  // Only scroll on desktop and if not already in view
  if (
    !isMobile &&
    bestSellerProducts &&
    !isElementInViewport(bestSellerProducts)
  ) {
    scrollToElement(bestSellerProducts);
  }
}

// Update scrollToShowResults to use the unified scrollToElement
function scrollToShowResults() {
  const showResultsBtn = document.getElementById("show-results");
  if (showResultsBtn && !showResultsBtn.classList.contains("hide-this")) {
    scrollToElement(showResultsBtn);
  }
}

// Replace scrollToFirstVisibleCard with scrollToFirstVisibleProduct
function scrollToFirstVisibleCard() {
  scrollToFirstVisibleProduct();
}

// Replace scrollToTopMobile with scrollToElement
function scrollToTopMobile() {
  if (isMobile) {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
}

function uncheckFilters(filterType) {
  const checkboxes = document.querySelectorAll(
    `.ombraz-accordion-content input[type="checkbox"][data-filter-type="${filterType}"]`
  );
  checkboxes.forEach((checkbox) => (checkbox.checked = false));
}

function uncheckLensOptions() {
  const lensOptionCheckboxes = document.querySelectorAll(
    '.ombraz-accordion-content input[type="checkbox"][data-filter-type="lens-option"]'
  );
  lensOptionCheckboxes.forEach((checkbox) => (checkbox.checked = false));
}

function getCheckedValues(filterType) {
  const checkedCheckboxes = document.querySelectorAll(
    `.ombraz-accordion-content input[type="checkbox"][data-filter-type="${filterType}"]:checked`
  );

  // Use the right attribute name based on filter type
  let attributeName;
  if (filterType === "frame-size") {
    attributeName = "data-frame-size";
  } else if (filterType === "gear") {
    attributeName = "data-gear";
  } else if (filterType === "frame-color") {
    attributeName = "data-frame-color";
  } else if (filterType === "lens-option") {
    attributeName = "data-lens-option";
  } else {
    attributeName = "data-frame-style";
  }

  return Array.from(checkedCheckboxes).map((cb) =>
    (cb.getAttribute(attributeName) || "").toLowerCase()
  );
}

function getAvailableSizes(item) {
  const sizes = [];

  // First check data attributes (best method)
  const sizeAttr = item.getAttribute("data-sizes");
  if (sizeAttr) {
    return sizeAttr
      .toLowerCase()
      .split(",")
      .map((s) => s.trim());
  }

  // Check data-size attribute
  const sizeSingle = item.getAttribute("data-size");
  if (sizeSingle) {
    sizes.push(sizeSingle.toLowerCase().trim());
    return sizes;
  }

  // Check for variant selectors with size information
  const sizeSelectors = item.querySelectorAll(".variant-size-selector");
  if (sizeSelectors && sizeSelectors.length > 0) {
    sizeSelectors.forEach((selector) => {
      const sizeValue = selector.getAttribute("data-value");
      if (sizeValue) {
        sizes.push(sizeValue.toLowerCase().trim());
      }
    });

    if (sizes.length > 0) {
      return sizes;
    }
  }

  // Fallback to checking input elements
  if (
    item.querySelector('input[id^="regular-"]') ||
    item.querySelector('[data-size="regular"]')
  ) {
    sizes.push("regular");
  }

  if (
    item.querySelector('input[id^="narrow-"]') ||
    item.querySelector('[data-size="narrow"]')
  ) {
    sizes.push("narrow");
  }

  if (
    item.querySelector('input[id^="xl-"]') ||
    item.querySelector('[data-size="xl"]')
  ) {
    sizes.push("xl");
  }

  return sizes;
}

function handleURLBasedFiltering() {
  // Check for URL parameters related to filtering
  const urlParams = new URLSearchParams(window.location.search);
  const currentPath = window.location.pathname;

  // Hide on-sale products by default on all pages except on-sale collection
  const isSalePage =
    currentPath.includes("/collections/onsale") ||
    currentPath.includes("/collections/on-sale");
  const onSaleSection = document.querySelector("[data-onsale-products]");
  const saleCheckbox = document.querySelector(
    '.ombraz-sale [data-collection="onsale"]'
  );

  if (onSaleSection && !isSalePage) {
    // Hide the on-sale products section on non-sale pages
    onSaleSection.classList.add("hide-this", "visually-hidden");

    // Ensure the sale checkbox is unchecked
    if (saleCheckbox) {
      saleCheckbox.checked = false;
    }
  }

  // First check for the sizes parameter which can contain comma-separated values
  if (urlParams.has("sizes")) {
    const sizeValues = urlParams.get("sizes").split(",");
    debugLog("Found sizes parameter with values:", sizeValues);

    // Apply each size filter
    sizeValues.forEach((size) => {
      // Check if the size is valid
      if (["narrow", "regular", "xl"].includes(size.toLowerCase())) {
        queryFilterBySize(size.toLowerCase());
      }
    });

    // Show filter UI elements
    toggleShowResultsButton(true);
    toggleClearButton(true);

    // Apply filters immediately
    applyFilters();

    return; // Exit early since we've handled the sizes parameter
  }

  // Continue with the rest of the function for other URL parameters
  // First check collection paths
  if (
    currentPath.includes("/collections/on-sale") ||
    currentPath.includes("/collections/onsale")
  ) {
    if (saleCheckbox) {
      saleCheckbox.checked = true;

      // Show on-sale products section
      if (onSaleSection) {
        onSaleSection.classList.remove("hide-this", "visually-hidden");
      }

      // For mobile, always apply the filter immediately when directly on sale page
      handleSaleFilter(true);

      // Show buttons
      toggleShowResultsButton(true);
      toggleClearButton(true);
    }
  } else if (currentPath.includes("/collections/best-sellers")) {
    // Hide on-sale products on best-sellers page
    if (onSaleSection) {
      onSaleSection.classList.add("hide-this", "visually-hidden");
    }

    setBestSellerFilter();
  } else if (currentPath.includes("/collections/non-polarized")) {
    setFilterOption("lens-option", "non-polarized");
  } else if (currentPath.includes("/collections/polarized")) {
    setFilterOption("lens-option", "polarized");
  } else if (currentPath.includes("/collections/blue-blocker")) {
    setFilterOption("lens-option", "blue-blocking");
  } else if (currentPath.includes("/collections/gear")) {
    if (urlParams.has("sideshields")) {
      setFilterOption("gear", "shields");
    } else if (urlParams.has("risers")) {
      setFilterOption("gear", "risers");
    } else {
      setAllGearOptions();
    }
  } else {
    // Check if any size parameter exists in the old format
    if (urlParams.has("narrow")) {
      queryFilterBySize("narrow");
    } else if (urlParams.has("regular")) {
      queryFilterBySize("regular");
    } else if (urlParams.has("xl")) {
      queryFilterBySize("xl");
    }

    // Check for frame style parameters
    if (urlParams.has("classic")) {
      setFilterOption("frame-style", "classic");
    } else if (urlParams.has("dolomite")) {
      setFilterOption("frame-style", "dolomite");
    } else if (urlParams.has("leggero")) {
      setFilterOption("frame-style", "leggero");
    } else if (urlParams.has("cammina")) {
      setFilterOption("frame-style", "cammina");
    } else if (urlParams.has("viale")) {
      setFilterOption("frame-style", "viale");
    } else if (urlParams.has("teton")) {
      setFilterOption("frame-style", "teton");
    }

    // Check for gear parameters
    if (urlParams.has("shields")) {
      setFilterOption("gear", "shields");
    } else if (urlParams.has("risers")) {
      setFilterOption("gear", "risers");
    } else if (urlParams.has("floatie")) {
      setFilterOption("gear", "floatie");
    } else if (urlParams.has("case")) {
      setFilterOption("gear", "case");
    } else if (urlParams.has("cordreplacement")) {
      setFilterOption("gear", "cordreplacement");
    } else if (urlParams.has("sticker")) {
      setFilterOption("gear", "sticker");
    } else if (urlParams.has("giftcard")) {
      setFilterOption("gear", "giftcard");
    }

    // Check for lens options
    if (urlParams.has("polarized")) {
      setFilterOption("lens-option", "polarized");
    } else if (urlParams.has("non-polarized")) {
      setFilterOption("lens-option", "non-polarized");
    } else if (urlParams.has("blue-blocking")) {
      setFilterOption("lens-option", "blue-blocking");
    }

    // Check for frame color
    if (urlParams.has("charcoal")) {
      setFilterOption("frame-color", "charcoal");
    } else if (urlParams.has("dusk")) {
      setFilterOption("frame-color", "dusk");
    } else if (urlParams.has("honey")) {
      setFilterOption("frame-color", "honey");
    } else if (urlParams.has("matte-brown")) {
      setFilterOption("frame-color", "matte-brown");
    } else if (urlParams.has("tortoise")) {
      setFilterOption("frame-color", "Tortoise");
    }

    // Check for best-seller and apply if needed
    if (urlParams.has("best-sellers")) {
      setBestSellerFilter();
    }

    // Check for on-sale filter
    if (urlParams.has("onsale")) {
      if (saleCheckbox) {
        saleCheckbox.checked = true;
        handleSaleFilter(true);
      }
    }
  }

  // If we're on the shop-all page, scroll to first visible card
  if (currentPath.includes("/collections/shop-all")) {
    setTimeout(() => {
      if (!isMobile) {
        scrollToFirstVisibleCard();
      }
    }, 300);
  }

  // If any filter is active, make sure the filter UI matches
  const hasActiveFilters =
    urlParams.has("narrow") ||
    urlParams.has("regular") ||
    urlParams.has("xl") ||
    urlParams.has("sizes") || // Add this to check for the sizes parameter
    urlParams.has("classic") ||
    urlParams.has("dolomite") ||
    urlParams.has("leggero") ||
    urlParams.has("cammina") ||
    urlParams.has("viale") ||
    urlParams.has("teton") ||
    urlParams.has("shields") ||
    urlParams.has("risers") ||
    urlParams.has("floatie") ||
    urlParams.has("case") ||
    urlParams.has("cordreplacement") ||
    urlParams.has("sticker") ||
    urlParams.has("giftcard") ||
    urlParams.has("polarized") ||
    urlParams.has("non-polarized") ||
    urlParams.has("blue-blocking") ||
    urlParams.has("charcoal") ||
    urlParams.has("dusk") ||
    urlParams.has("honey") ||
    urlParams.has("matte-brown") ||
    urlParams.has("tortoise") ||
    urlParams.has("best-sellers") ||
    urlParams.has("onsale") ||
    currentPath.includes("/collections/on-sale") ||
    currentPath.includes("/collections/onsale") ||
    currentPath.includes("/collections/best-sellers") ||
    currentPath.includes("/collections/non-polarized") ||
    currentPath.includes("/collections/polarized") ||
    currentPath.includes("/collections/blue-blocker") ||
    currentPath.includes("/collections/gear");

  if (hasActiveFilters) {
    // Ensure the filter buttons show correctly
    toggleShowResultsButton(true);
    toggleClearButton(true);

    // Apply filters immediately for mobile
    if (isMobile) {
      applyFilters();
    }
  }
}

function setBestSellerFilter() {
  const bestSellerCheckbox = document.querySelector(
    '.ombraz-best-seller input[type="checkbox"]'
  );
  if (bestSellerCheckbox) {
    bestSellerCheckbox.checked = true;
    handleBestSellerFilter();
  }
}

function setFilterOption(filterType, optionValue) {
  let attributeName;
  if (filterType === "frame-size") {
    attributeName = "data-frame-size";
  } else if (filterType === "gear") {
    attributeName = "data-gear";
  } else if (filterType === "frame-color") {
    attributeName = "data-frame-color";
  } else if (filterType === "lens-option") {
    attributeName = "data-lens-option";
  } else {
    attributeName = "data-frame-style";
  }

  const checkbox = document.querySelector(
    `input[type="checkbox"][data-filter-type="${filterType}"][${attributeName}="${optionValue}"]`
  );

  if (checkbox) {
    const details = checkbox.closest(".ombraz-accordion-item");
    if (details) {
      details.setAttribute("open", "");
      updateAccordionIcon(details, true);
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event("change"));

      // Show buttons on mobile
      if (isMobile) {
        toggleShowResultsButton(true);
        toggleClearButton(true);
      }
    }
  }
}

function setAllGearOptions() {
  const gearCheckboxes = document.querySelectorAll(
    'input[type="checkbox"][data-filter-type="gear"]'
  );
  const gearDetails = gearCheckboxes[0]?.closest(".ombraz-accordion-item");
  if (gearDetails) {
    gearDetails.setAttribute("open", "");
    updateAccordionIcon(gearDetails, true);
    gearCheckboxes.forEach((checkbox) => {
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event("change"));
    });

    // Show buttons on mobile
    if (isMobile) {
      toggleShowResultsButton(true);
      toggleClearButton(true);
    }
  }
}

function updateAccordionIcon(accordionItem, isOpen) {
  const icon = accordionItem.querySelector(".ombraz-icon");
  if (icon) {
    if (isOpen) {
      icon.classList.remove("ombraz-icon-plus");
      icon.classList.add("ombraz-icon-minus");
    } else {
      icon.classList.remove("ombraz-icon-minus");
      icon.classList.add("ombraz-icon-plus");
    }
  }
}

function initializeAccordion() {
  const accordionItems = document.querySelectorAll(".ombraz-accordion-item");

  accordionItems.forEach((item) => {
    const summary = item.querySelector("summary");
    const icon = summary?.querySelector(".ombraz-icon");

    if (summary) {
      // Set initial icon state
      if (icon) {
        const isOpen = item.hasAttribute("open");
        icon.classList.toggle("ombraz-icon-minus", isOpen);
        icon.classList.toggle("ombraz-icon-plus", !isOpen);
      }

      summary.addEventListener("click", (e) => {
        e.preventDefault();
        const isOpen = item.hasAttribute("open");

        // Close all other accordion items
        accordionItems.forEach((otherItem) => {
          if (otherItem !== item && otherItem.hasAttribute("open")) {
            otherItem.removeAttribute("open");
            const otherIcon = otherItem.querySelector(".ombraz-icon");
            if (otherIcon) {
              otherIcon.classList.remove("ombraz-icon-minus");
              otherIcon.classList.add("ombraz-icon-plus");
            }
          }
        });

        // Toggle current accordion
        item.toggleAttribute("open");

        // Update icon
        if (icon) {
          if (!isOpen) {
            icon.classList.remove("ombraz-icon-plus");
            icon.classList.add("ombraz-icon-minus");
          } else {
            icon.classList.remove("ombraz-icon-minus");
            icon.classList.add("ombraz-icon-plus");
          }
        }
      });
    }
  });
}

function clearAllFilters() {
  const clearButtonDesktop = document.getElementById("clear-all");
  const clearButtonMobile = document.getElementById("clear-all-mobile");

  function handleClearAll() {
    const allCheckboxes = document.querySelectorAll(
      '.ombraz-accordion-content input[type="checkbox"]:checked, .ombraz-best-seller input[type="checkbox"]:checked, .ombraz-sale input[type="checkbox"]:checked'
    );

    // Get current URL path to check if we're on a collection page
    const currentPath = window.location.pathname;
    const isOnCollectionPage =
      currentPath.includes("/collections/polarized") ||
      currentPath.includes("/collections/non-polarized") ||
      currentPath.includes("/collections/blue-blocker");

    // Get sale checkbox and best seller checkbox
    const saleCheckbox = document.querySelector(
      '.ombraz-sale input[type="checkbox"]'
    );
    const bestSellerCheckbox = document.querySelector(
      '.ombraz-best-seller input[type="checkbox"]'
    );
    const regularProductGrid = document.querySelector(
      ".ombraz-product-v2__grid"
    );
    const saleProducts = document.querySelector("[data-onsale-products]");
    const bestSellerProducts = document.querySelector("[data-custom-products]");

    if (allCheckboxes.length) {
      allCheckboxes.forEach((checkbox) => {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event("change"));
      });
    }

    // Clear Best Seller filter
    if (bestSellerCheckbox && bestSellerCheckbox.checked) {
      bestSellerCheckbox.checked = false;
      handleBestSellerFilter();
    }

    // Clear Sale filter
    if (saleCheckbox && saleCheckbox.checked) {
      saleCheckbox.checked = false;
      handleSaleFilter();
    }

    // Show regular product grid and hide special sections
    if (regularProductGrid) {
      regularProductGrid.classList.remove("hide-this");
      regularProductGrid.classList.remove("visually-hidden");

      // Show all products in regular grid
      const regularProducts = regularProductGrid.querySelectorAll(
        ".ombraz-product-v2__grid-item"
      );
      regularProducts.forEach((product) => {
        product.classList.remove("hide-this");
        product.classList.remove("hide-this-by-filter");
      });
    }

    // Hide sale products section
    if (saleProducts) {
      saleProducts.classList.add("hide-this");
      saleProducts.classList.add("visually-hidden");
    }

    // Hide best seller products section
    if (bestSellerProducts) {
      bestSellerProducts.classList.add("hide-this");
    }

    // Hide both buttons
    toggleClearButton(false);
    toggleShowResultsButton(false);

    // Clear URL parameters without page reload
    window.history.replaceState({}, "", window.location.pathname);

    // If we're on a special lens collection page, show those items by their lens type
    if (isOnCollectionPage) {
      // Reset any hiding that might have been applied
      resetAllProductVisibility();

      // Find the lens option that should be visible based on the collection page
      let lensType = "";
      if (currentPath.includes("/collections/polarized")) {
        lensType = "polarized";
      } else if (currentPath.includes("/collections/non-polarized")) {
        lensType = "non-polarized";
      } else if (currentPath.includes("/collections/blue-blocker")) {
        lensType = "blue-blocking";
      }

      // Show only products matching the lens type of current collection
      if (lensType && regularProductGrid) {
        const products = regularProductGrid.querySelectorAll(
          ".ombraz-product-v2__grid-item"
        );

        // First show all products to avoid flickering
        products.forEach((product) => {
          product.classList.remove("hide-this");
          product.classList.remove("hide-this-by-filter");
        });

        // Then hide products that don't match the lens type
        requestAnimationFrame(() => {
          products.forEach((product) => {
            const itemLensValue = (
              product.getAttribute("data-lens-value") || ""
            ).toLowerCase();

            let showItem = false;
            if (lensType === "polarized") {
              showItem =
                itemLensValue.includes("polarized") &&
                !itemLensValue.includes("non-polarized");
            } else if (lensType === "non-polarized") {
              showItem = itemLensValue.includes("non-polarized");
            } else if (lensType === "blue-blocking") {
              showItem = itemLensValue.includes("blue-blocking");
            }

            if (showItem) {
              product.classList.remove("hide-this");
              product.classList.remove("hide-this-by-filter");
            } else {
              product.classList.add("hide-this");
              product.classList.add("hide-this-by-filter");
            }
          });

          // Wait a bit for DOM to update, then smooth scroll to the first visible item
          setTimeout(() => {
            const firstVisibleItem = regularProductGrid.querySelector(
              ".ombraz-product-v2__grid-item:not(.hide-this)"
            );
            if (firstVisibleItem) {
              const collectionContainer = document.querySelector(
                ".ombraz__collection-container"
              );
              if (collectionContainer) {
                collectionContainer.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            }
          }, 100);
        });
      }
    } else {
      // For desktop, smoothly scroll to the collection container
      if (!window.matchMedia("(max-width: 1199px)").matches) {
        const collectionContainer = document.querySelector(
          ".ombraz__collection-container"
        );
        if (collectionContainer) {
          setTimeout(() => {
            collectionContainer.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 100);
        }
      }
    }

    // Hide "No results" message if it exists
    displayNoResultsMessage(false);
  }

  // Desktop clear all
  if (clearButtonDesktop) {
    clearButtonDesktop.addEventListener("click", function () {
      handleClearAll();
      if (!isMobile) {
        applyFilters();
      }

      // Clear URL parameters
      window.history.replaceState({}, "", window.location.pathname);
    });
  }

  // Mobile clear all
  if (clearButtonMobile) {
    clearButtonMobile.addEventListener("click", function () {
      handleClearAll();
      if (isMobile) {
        applyFilters(); // Reset the view
        closeTheFilterHederMobile();

        // Add delay to ensure DOM updates are complete
        setTimeout(() => {
          const firstVisibleCard = document.querySelector(
            ".ombraz-product-v2__grid-item:not(.hide-this)"
          );
          if (firstVisibleCard) {
            scrollToElement(firstVisibleCard);
          }
        }, 100);
      }

      // Clear URL parameters
      window.history.replaceState({}, "", window.location.pathname);
    });
  }
}

function handleBestSellerFilter(fromShowResultsButton = false) {
  debugLog(
    `handleBestSellerFilter called with fromShowResultsButton=${fromShowResultsButton}`
  );

  const bestSellerCheckbox = document.querySelector(
    '.ombraz-best-seller input[type="checkbox"]'
  );
  const bestSellerProducts = document.querySelector("[data-custom-products]");
  const saleCheckbox = document.querySelector(
    '.ombraz-sale input[type="checkbox"]'
  );
  const regularProductGrid = document.querySelector(
    ".ombraz-product-v2__grid[data-is-new-ui]"
  );
  const saleProducts = document.querySelector("[data-onsale-products]");

  // For mobile, if not from show results button or URL params, just return
  // This prevents the filter from being applied when the checkbox is clicked
  if (isMobile && !fromShowResultsButton && bestSellerCheckbox?.checked) {
    debugLog("On mobile: waiting for Show Results button");

    // Ensure other filters are properly handled
    if (saleCheckbox && saleCheckbox.checked) {
      saleCheckbox.checked = false;
      if (saleProducts) {
        saleProducts.classList.add("hide-this", "visually-hidden");
      }
    }

    // Show Show Results button
    toggleShowResultsButton(true);
    toggleClearButton(true);

    // Update URL parameters
    saveFiltersToURL();

    return;
  }

  // Get all checked filter values
  const checkedFrameSizes = getActiveFilters("frame-size");
  const checkedFrameStyles = getActiveFilters("frame-style");
  const checkedLensOptions = getActiveFilters("lens-option");
  const checkedFrameColors = getActiveFilters("frame-color");
  const checkedGearItems = getActiveFilters("gear");

  const hasActiveFilters =
    checkedFrameSizes.length > 0 ||
    checkedFrameStyles.length > 0 ||
    checkedLensOptions.length > 0 ||
    checkedFrameColors.length > 0 ||
    checkedGearItems.length > 0;

  if (bestSellerCheckbox && bestSellerCheckbox.checked) {
    // Uncheck Sale checkbox when Best Seller is checked
    if (saleCheckbox && saleCheckbox.checked) {
      saleCheckbox.checked = false;
    }

    // Hide sale products section completely
    if (saleProducts) {
      saleProducts.classList.add("hide-this");
      saleProducts.classList.add("visually-hidden");
    }

    // Show Best Seller products
    if (bestSellerProducts) {
      bestSellerProducts.classList.remove("hide-this");
      bestSellerProducts.classList.remove("visually-hidden");

      // Filter best seller products if other filters are active
      if (hasActiveFilters) {
        const bsItems = bestSellerProducts.querySelectorAll(
          ".bs-product-card-v2__list-item"
        );

        let visibleItemCount = 0;

        // Function to check if a product matches all active filters
        const productMatchesFilters = (product) => {
          let matchesAllFilters = true;

          // Check frame size
          if (checkedFrameSizes.length > 0) {
            const itemSize = product.getAttribute("data-size") || "";
            const matchesSize = checkedFrameSizes.some((size) =>
              itemSize.includes(size.toLowerCase())
            );
            if (!matchesSize) matchesAllFilters = false;
          }

          // Check frame style
          if (matchesAllFilters && checkedFrameStyles.length > 0) {
            const itemStyle = (
              product.getAttribute("data-style") || ""
            ).toLowerCase();
            if (!checkedFrameStyles.includes(itemStyle)) {
              matchesAllFilters = false;
            }
          }

          // Check lens options
          if (matchesAllFilters && checkedLensOptions.length > 0) {
            const itemLensValue = (
              product.getAttribute("data-lens-value") || ""
            ).toLowerCase();
            const matchesLensOption = checkedLensOptions.some((option) => {
              if (option === "polarized") {
                return (
                  itemLensValue.includes("polarized") &&
                  !itemLensValue.includes("non-polarized")
                );
              } else if (option === "non-polarized") {
                return itemLensValue.includes("non-polarized");
              } else if (option === "blue-blocking") {
                return itemLensValue.includes("blue-blocking");
              }
              return itemLensValue.includes(option);
            });
            if (!matchesLensOption) matchesAllFilters = false;
          }

          // Check frame color
          if (matchesAllFilters && checkedFrameColors.length > 0) {
            const itemColor = (
              product.getAttribute("data-color") || ""
            ).toLowerCase();
            if (!checkedFrameColors.includes(itemColor)) {
              matchesAllFilters = false;
            }
          }

          // Check gear items (unlikely to apply to best seller products, but included for consistency)
          if (matchesAllFilters && checkedGearItems.length > 0) {
            matchesAllFilters = false; // Best seller products are not gear items
          }

          return matchesAllFilters;
        };

        // Apply filtering to best seller items
        bsItems.forEach((item) => {
          if (productMatchesFilters(item)) {
            item.classList.remove("hide-this");
            visibleItemCount++;
          } else {
            item.classList.add("hide-this");
          }
        });

        // Show "No Results" message if no items are visible and filters are active
        const showNoResults = visibleItemCount === 0 && hasActiveFilters;
        displayNoResultsMessage(showNoResults);
      } else {
        // No active filters, show all best seller products
        const bsItems = bestSellerProducts.querySelectorAll(
          ".bs-product-card-v2__list-item"
        );
        bsItems.forEach((item) => {
          item.classList.remove("hide-this");
        });

        // Make sure "No Results" message is hidden
        displayNoResultsMessage(false);
      }

      if (!isMobile) {
        // On desktop, scroll to a specific position for optimal product visibility
        setTimeout(() => {
          window.scrollTo({
            top: 653.5,
            behavior: "smooth",
          });
        }, 100);
      } else if (fromShowResultsButton) {
        // On mobile, scroll to top of products after clicking Show Results
        setTimeout(() => {
          closeTheFilterHederMobile();
          window.scrollTo({
            top: 500, // Adjusted for mobile
            behavior: "smooth",
          });
        }, 100);
      }
    }

    // Hide regular product grid completely
    if (regularProductGrid) {
      regularProductGrid.classList.add("hide-this");
      regularProductGrid.classList.add("visually-hidden");

      // Also hide all items inside the grid
      regularProductGrid
        .querySelectorAll(".ombraz-product-v2__grid-item")
        .forEach((product) => {
          product.classList.add("hide-this");
        });
    }
  } else {
    // Hide Best Seller products
    if (bestSellerProducts) {
      bestSellerProducts.classList.add("hide-this");
    }

    // Show regular product grid
    if (regularProductGrid) {
      regularProductGrid.classList.remove("hide-this");
      regularProductGrid.classList.remove("visually-hidden");

      // Show all regular products
      regularProductGrid
        .querySelectorAll(".ombraz-product-v2__grid-item")
        .forEach((product) => {
          product.classList.remove("hide-this");
        });
    }

    // Only scroll on desktop
    if (!isMobile) {
      scrollToFirstVisibleCard();
    }
  }

  // Update UI states
  toggleShowResultsButton(bestSellerCheckbox.checked || hasActiveFilters);
  toggleClearButton(bestSellerCheckbox.checked || hasActiveFilters);

  // Update URL parameters
  saveFiltersToURL();
}

function scrollToFirstVisibleCard() {
  scrollToFirstVisibleProduct();
}

function toggleShowResultsButton(show) {
  const showResultsBtn = document.getElementById("show-results");
  if (showResultsBtn) {
    showResultsBtn.classList.toggle("hide-this", !show);
  }
}

function scrollToTopMobile() {
  if (isMobile) {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
}

function toggleClearButton(show) {
  const clearButtonDesktop = document.getElementById("clear-all");
  const clearButtonMobile = document.getElementById("clear-all-mobile");

  if (clearButtonDesktop) {
    clearButtonDesktop.classList.toggle("hide-this", !show);
  }

  if (clearButtonMobile) {
    clearButtonMobile.classList.toggle("hide-this", !show);
  }
}

function closeTheFilterHederMobile() {
  const sidebar = document.getElementById("ombrazSidebar");
  const filterWrapper = document.getElementById("ombrazFilterWrapper");
  if (sidebar) {
    sidebar.classList.remove("active");
    if (filterWrapper) {
      filterWrapper.classList.remove("is-full-screen");
    }

    // Restore scroll position and unlock body
    const scrollY = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--scroll-position"
      )
    );
    document.body.classList.remove("filter-active");
    document.documentElement.style.removeProperty("--scroll-position");
    window.scrollTo(0, Math.abs(scrollY));
  }
}

function uncheckAllFilters() {
  const allCheckboxes = document.querySelectorAll(
    '.ombraz-accordion-content input[type="checkbox"], .ombraz-best-seller input[type="checkbox"]'
  );
  allCheckboxes.forEach((checkbox) => {
    checkbox.checked = false;
  });
}

function scrollToShowResults() {
  const showResultsBtn = document.getElementById("show-results");
  if (showResultsBtn && !showResultsBtn.classList.contains("hide-this")) {
    scrollToElement(showResultsBtn);
  }
}

function handleSaleFilter(fromShowResultsButton = false) {
  debugLog(
    `handleSaleFilter called with fromShowResultsButton=${fromShowResultsButton}`
  );

  const saleCheckbox = document.querySelector(
    '.ombraz-sale input[type="checkbox"]'
  );
  const saleProducts = document.querySelector("[data-onsale-products]");
  const bestSellerCheckbox = document.querySelector(
    '.ombraz-best-seller input[type="checkbox"]'
  );
  const regularProductGrid = document.querySelector(".ombraz-product-v2__grid");
  const bestSellerProducts = document.querySelector("[data-custom-products]");

  if (!saleCheckbox || !saleCheckbox.checked) {
    // Hide Sale products section
    if (saleProducts) {
      saleProducts.classList.add("hide-this");
      saleProducts.classList.add("visually-hidden");
    }

    // Show regular product grid
    if (regularProductGrid) {
      regularProductGrid.classList.remove("hide-this");
      regularProductGrid.classList.remove("visually-hidden");
    }

    // Get all checked filter values
    const checkedFrameSizes = getActiveFilters("frame-size");
    const checkedFrameStyles = getActiveFilters("frame-style");
    const checkedLensOptions = getActiveFilters("lens-option");
    const checkedFrameColors = getActiveFilters("frame-color");
    const checkedGearItems = getActiveFilters("gear");

    const hasActiveFilters =
      checkedFrameSizes.length > 0 ||
      checkedFrameStyles.length > 0 ||
      checkedLensOptions.length > 0 ||
      checkedFrameColors.length > 0 ||
      checkedGearItems.length > 0;

    // If other filters are active, reapply them
    if (hasActiveFilters) {
      applyFilters();
    } else if (bestSellerCheckbox && bestSellerCheckbox.checked) {
      // If Best Seller is checked, handle that
      handleBestSellerFilter();
    } else {
      // Show all regular products if no filters are active
      if (regularProductGrid) {
        const regularProducts = regularProductGrid.querySelectorAll(
          ".ombraz-product-v2__grid-item"
        );
        regularProducts.forEach((product) => {
          product.classList.remove("hide-this");
        });
      }

      // Hide "No results" message
      displayNoResultsMessage(false);

      // Scroll to the regular product grid
      if (!isMobile) {
        setTimeout(() => {
          scrollToFirstVisibleCard();
        }, 100);
      }
    }

    // Update UI states based on the remaining active filters
    const hasAnyFilter =
      hasActiveFilters || (bestSellerCheckbox && bestSellerCheckbox.checked);
    toggleShowResultsButton(hasAnyFilter);
    toggleClearButton(hasAnyFilter);

    // Update URL parameters
    saveFiltersToURL();

    return;
  }

  // For mobile, only show the Show Results button and don't apply filter immediately
  // Skip this check if we're coming from the Show Results button click or URL params
  if (isMobile && !fromShowResultsButton) {
    // Only uncheck Best Seller when Sale is checked
    if (bestSellerCheckbox && bestSellerCheckbox.checked) {
      bestSellerCheckbox.checked = false;
      if (bestSellerProducts) {
        bestSellerProducts.classList.add("hide-this");
      }
    }

    // Show "Show Results" button
    toggleShowResultsButton(true);
    toggleClearButton(true);

    // Scroll to "Show Results" button
    scrollToShowResults();

    // Update URL parameters even if we haven't applied the filter yet
    saveFiltersToURL();

    // Exit early - don't apply filter until Show Results is clicked
    return;
  }

  // Only uncheck Best Seller when Sale is checked
  if (bestSellerCheckbox && bestSellerCheckbox.checked) {
    bestSellerCheckbox.checked = false;
    if (bestSellerProducts) {
      bestSellerProducts.classList.add("hide-this");
    }
  }

  // Show Sale products section
  if (saleProducts) {
    saleProducts.classList.remove("hide-this");
    saleProducts.classList.remove("visually-hidden");
  }

  // Get all checked filter values
  const checkedFrameSizes = getActiveFilters("frame-size");
  const checkedFrameStyles = getActiveFilters("frame-style");
  const checkedLensOptions = getActiveFilters("lens-option");
  const checkedFrameColors = getActiveFilters("frame-color");
  const checkedGearItems = getActiveFilters("gear");

  const hasActiveFilters =
    checkedFrameSizes.length > 0 ||
    checkedFrameStyles.length > 0 ||
    checkedLensOptions.length > 0 ||
    checkedFrameColors.length > 0 ||
    checkedGearItems.length > 0;

  // Function to check if a product matches all active filters
  const productMatchesFilters = (product) => {
    if (!hasActiveFilters) return true;

    let matchesAllFilters = true;

    // Check frame size
    if (checkedFrameSizes.length > 0) {
      const itemSizes = getAvailableSizes(product);
      const matchesSize = checkedFrameSizes.some((size) =>
        itemSizes.includes(size.toLowerCase())
      );
      if (!matchesSize) matchesAllFilters = false;
    }

    // Check frame style
    if (matchesAllFilters && checkedFrameStyles.length > 0) {
      const itemStyle = (
        product.getAttribute("data-style") || ""
      ).toLowerCase();
      if (!checkedFrameStyles.includes(itemStyle)) {
        matchesAllFilters = false;
      }
    }

    // Check lens options
    if (matchesAllFilters && checkedLensOptions.length > 0) {
      const itemLensValue = (
        product.getAttribute("data-lens-value") || ""
      ).toLowerCase();
      const matchesLensOption = checkedLensOptions.some((option) => {
        if (option === "polarized") {
          return (
            itemLensValue.includes("polarized") &&
            !itemLensValue.includes("non-polarized")
          );
        } else if (option === "non-polarized") {
          return itemLensValue.includes("non-polarized");
        } else if (option === "blue-blocking") {
          return itemLensValue.includes("blue-blocking");
        }
        return itemLensValue.includes(option);
      });
      if (!matchesLensOption) matchesAllFilters = false;
    }

    // Check frame color
    if (matchesAllFilters && checkedFrameColors.length > 0) {
      const itemColor = (
        product.getAttribute("data-color") || ""
      ).toLowerCase();
      if (!checkedFrameColors.includes(itemColor)) {
        matchesAllFilters = false;
      }
    }

    // Check gear items
    if (matchesAllFilters && checkedGearItems.length > 0) {
      const itemGear = (product.getAttribute("data-gear") || "").toLowerCase();
      if (!checkedGearItems.includes(itemGear)) {
        matchesAllFilters = false;
      }
    }

    return matchesAllFilters;
  };

  // Hide the regular product grid completely (unlike before where we were filtering it)
  if (regularProductGrid) {
    regularProductGrid.classList.add("hide-this");
    regularProductGrid.classList.add("visually-hidden");
  }

  // Filter only sale products section
  let visibleCount = 0;
  if (saleProducts) {
    const saleItems = saleProducts.querySelectorAll(".is-onsale-product-card");
    saleItems.forEach((product) => {
      if (productMatchesFilters(product)) {
        product.classList.remove("hide-this");
        visibleCount++;
      } else {
        product.classList.add("hide-this");
      }
    });
  }

  // Show or hide "No results" message
  displayNoResultsMessage(visibleCount === 0 && hasActiveFilters);

  // Replace scrollToVisibleProducts with direct scrolling
  if (!isMobile) {
    setTimeout(() => {
      // Scroll to the ideal position for product visibility
      window.scrollTo({
        top: 653.5,
        behavior: "smooth",
      });
    }, 100);
  } else if (isMobile && fromShowResultsButton) {
    // On mobile, close the filter header and scroll to a good position
    setTimeout(() => {
      closeTheFilterHederMobile();
      window.scrollTo({
        top: 500, // Adjusted for mobile
        behavior: "smooth",
      });
    }, 100);
  }

  // Update UI states
  toggleShowResultsButton(true);
  toggleClearButton(true);

  // Update URL parameters
  saveFiltersToURL();
}

function toggleFilter() {
  const sidebar = document.getElementById("ombrazSidebar");
  const filterWrapper = document.getElementById("ombrazFilterWrapper");

  if (sidebar) {
    const isActive = sidebar.classList.contains("active");

    if (!isActive) {
      // Opening the filter
      sidebar.classList.add("active");
      if (filterWrapper) {
        filterWrapper.classList.add("is-full-screen");
      }

      // Store current scroll position and lock body
      document.documentElement.style.setProperty(
        "--scroll-position",
        `-${window.scrollY}px`
      );
      document.body.classList.add("filter-active");
    } else {
      // Closing the filter
      closeTheFilterHederMobile();
    }
  }
}

// Function to query and filter products by size from URL parameter
function queryFilterBySize(size) {
  debugLog(`Filtering by size: ${size}`);

  // Normalize the size value
  const normalizedSize = size.toLowerCase().trim();

  // Get all product cards
  const productCards = document.querySelectorAll(
    ".ombraz-product-v2__grid-item"
  );

  // Track if we've found the checkbox for this size
  let foundCheckbox = false;

  // Find and check the corresponding size checkbox
  const sizeCheckbox = document.querySelector(
    `input[type="checkbox"][data-filter-type="frame-size"][data-frame-size="${normalizedSize}"]`
  );

  if (sizeCheckbox) {
    foundCheckbox = true;
    debugLog(`Found checkbox for size: ${normalizedSize}`);

    // Open the accordion containing this checkbox
    const accordion = sizeCheckbox.closest(".ombraz-accordion-item");
    if (accordion) {
      accordion.setAttribute("open", "");
      updateAccordionIcon(accordion, true);
    }

    // Check the checkbox
    sizeCheckbox.checked = true;

    // Don't trigger change event yet - we'll apply all filters at once
  } else {
    debugLog(`Could not find checkbox for size: ${normalizedSize}`);
  }

  // If we didn't find a checkbox, we'll manually filter the products
  if (!foundCheckbox) {
    debugLog(`Manually filtering products by size: ${normalizedSize}`);

    productCards.forEach((card) => {
      // Use our improved size detection function
      const itemSizes = getAvailableSizes(card);
      const hasSize = itemSizes.includes(normalizedSize);

      // Check if this is a gear/accessory item (which don't have sizes)
      const isGearOrAccessory =
        card.getAttribute("data-style")?.includes("shields") ||
        card.getAttribute("data-style")?.includes("riser") ||
        card.getAttribute("data-gear")?.includes("shields") ||
        card.getAttribute("data-gear")?.includes("risers") ||
        card.getAttribute("data-gear")?.includes("case") ||
        card.getAttribute("data-gear")?.includes("floatie") ||
        card.getAttribute("data-gear")?.includes("cordreplacement") ||
        card.getAttribute("data-gear")?.includes("sticker") ||
        card.getAttribute("data-gear")?.includes("giftcard");

      // Hide items that don't match the size or are gear items
      if (isGearOrAccessory || !hasSize) {
        card.classList.add("hide-this");
      }
    });
  }

  return foundCheckbox;
}

// Helper function to reset visibility of all products in the grid
function resetAllProductVisibility() {
  const productGrid = document.querySelector(".ombraz-product-v2__grid");
  if (productGrid) {
    const products = productGrid.querySelectorAll(
      ".ombraz-product-v2__grid-item"
    );
    products.forEach((product) => {
      product.classList.remove("hide-this");
      product.classList.remove("hide-this-by-filter");
    });
  }
}

// Function to handle direct size URL parameters
function handleSizeURLParameter() {
  const urlParams = new URLSearchParams(window.location.search);

  // Check if the sizes parameter exists
  if (urlParams.has("sizes")) {
    const sizeValues = urlParams.get("sizes").split(",");
    debugLog("Handling direct size URL parameter with values:", sizeValues);

    // Process each size value
    let foundAnySize = false;

    sizeValues.forEach((size) => {
      const normalizedSize = size.toLowerCase().trim();

      // Only process valid sizes
      if (["narrow", "regular", "xl"].includes(normalizedSize)) {
        // Try to find and check the corresponding checkbox
        const found = queryFilterBySize(normalizedSize);
        foundAnySize = foundAnySize || found;
      }
    });

    // If we found and checked any size checkboxes, apply the filters
    if (foundAnySize) {
      // Show filter UI elements
      toggleShowResultsButton(true);
      toggleClearButton(true);

      // Apply filters
      applyFilters();

      // Scroll to the first visible product
      setTimeout(() => {
        scrollToFirstVisibleProduct();
      }, 300);

      return true; // Indicate that we handled the parameter
    }
  }

  return false; // Indicate that we didn't handle any parameter
}
